import APICall from '../APICall';

const PERFORMANCE_ENDPOINTS = {
  PERFORMANCE: '/performance',
  HEALTH: '/performance/health',
  RESOURCES: '/performance/resources',
};

/**
 * Performance API Service
 * Handles all performance monitoring related API calls
 */
class PerformanceApiService {
  /**
   * Fetch real-time performance metrics
   * @returns {Promise<Object>} Performance metrics data
   */
  async getPerformanceMetrics() {
    try {
      const response = await APICall('get', PERFORMANCE_ENDPOINTS.PERFORMANCE);
      return this.transformPerformanceData(response?.data?.data || response?.data);
    } catch (error) {
      console.error('Error fetching performance metrics:', error);
      throw this.handleApiError(error);
    }
  }

  /**
   * Fetch service health status
   * @returns {Promise<Object>} Health status data
   */
  async getHealthStatus() {
    try {
      const response = await APICall('get', PERFORMANCE_ENDPOINTS.HEALTH);
      return this.transformHealthData(response?.data?.data || response?.data);
    } catch (error) {
      console.error('Error fetching health status:', error);
      throw this.handleApiError(error);
    }
  }

  /**
   * Fetch system resource usage
   * @returns {Promise<Object>} Resource usage data
   */
  async getResourceUsage() {
    try {
      const response = await APICall('get', PERFORMANCE_ENDPOINTS.RESOURCES);
      return this.transformResourceData(response?.data?.data || response?.data);
    } catch (error) {
      console.error('Error fetching resource usage:', error);
      throw this.handleApiError(error);
    }
  }

  /**
   * Fetch all performance data in parallel
   * @returns {Promise<Object>} Combined performance data
   */
  async getAllPerformanceData() {
    try {
      const [performance, health, resources] = await Promise.allSettled([
        this.getPerformanceMetrics(),
        this.getHealthStatus(),
        this.getResourceUsage(),
      ]);

      return {
        data: {
          performance: performance.status === 'fulfilled' ? performance.value : null,
          health: health.status === 'fulfilled' ? health.value : null,
          resources: resources.status === 'fulfilled' ? resources.value : null,
        },
        errors: {
          performance: performance.status === 'rejected' ? performance.reason : null,
          health: health.status === 'rejected' ? health.reason : null,
          resources: resources.status === 'rejected' ? resources.reason : null,
        },
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      console.error('Error fetching all performance data:', error);
      throw this.handleApiError(error);
    }
  }

  /**
   * Transform performance data to standardized format
   * @param {Object} data - Raw performance data
   * @returns {Object} Transformed performance data
   */
  transformPerformanceData(data) {
    // Calculate memory percentages
    const processHeapUsed = data?.memory?.process?.heapUsed || 0;
    const processHeapTotal = data?.memory?.process?.heapTotal || 0;
    const processHeapPercentage = processHeapTotal > 0 ? (processHeapUsed / processHeapTotal) * 100 : 0;
    
    const systemMemoryUsed = data?.memory?.system?.used || 0;
    const systemMemoryTotal = data?.memory?.system?.total || 0;
    const systemMemoryPercentage = systemMemoryTotal > 0 ? (systemMemoryUsed / systemMemoryTotal) * 100 : 0;
    
    // Calculate CPU usage percentage (total is already a percentage)
    const cpuUsage = data?.cpu?.process?.total || 0;
    
    // Calculate success rate
    const totalRequests = data?.requests?.totalRequests || 0;
    const totalErrors = data?.requests?.totalErrors || 0;
    const successRate = totalRequests > 0 ? ((totalRequests - totalErrors) / totalRequests) * 100 : 100;
    
    return {
      eventLoopLatency: {
        current: data?.eventLoopLatency?.current || 0,
        p95: data?.eventLoopLatency?.p95 || 0,
        p99: data?.eventLoopLatency?.p99 || 0,
        unit: 'ms',
      },
      memory: {
        processHeap: {
          used: processHeapUsed,
          total: processHeapTotal,
          percentage: processHeapPercentage,
        },
        systemMemory: {
          used: systemMemoryUsed,
          total: systemMemoryTotal,
          percentage: systemMemoryPercentage,
        },
      },
      cpu: {
        usage: cpuUsage,
        loadAverage: [
          data?.cpu?.system?.loadAverage?.['1min'] || 0,
          data?.cpu?.system?.loadAverage?.['5min'] || 0,
          data?.cpu?.system?.loadAverage?.['15min'] || 0,
        ],
      },
      requests: {
        total: totalRequests,
        errors: totalErrors,
        successRate: successRate,
        averageResponseTime: 0, // Not provided in your API
      },
      uptime: data?.uptime?.process || 0,
      timestamp: data?.timestamp || new Date().toISOString(),
    };
  }

  /**
   * Transform health data to standardized format
   * @param {Object} data - Raw health data
   * @returns {Object} Transformed health data
   */
  transformHealthData(data) {
    return {
      status: data?.status || 'unknown',
      services: {
        database: {
          status: data?.services?.database?.status || 'unknown',
          responseTime: 0, // Not provided in your API
          lastCheck: data?.timestamp || null,
        },
        redis: {
          status: data?.services?.redis?.status || 'unknown',
          responseTime: 0, // Not provided in your API
          lastCheck: data?.timestamp || null,
        },
        pnlProcessor: {
          status: data?.services?.services?.pnlProcessor === 'active' ? 'healthy' : 'unhealthy',
          responseTime: 0,
          lastCheck: data?.timestamp || null,
        },
        socketHandlers: {
          status: data?.services?.services?.socketHandlers === 'active' ? 'healthy' : 'unhealthy',
          responseTime: 0,
          lastCheck: data?.timestamp || null,
        },
        api: {
          status: 'healthy', // Assume API is healthy if we got the response
          responseTime: 0,
          lastCheck: data?.timestamp || null,
        },
      },
      uptime: 0, // Not provided in health API
      lastCheck: data?.timestamp || new Date().toISOString(),
    };
  }

  /**
   * Transform resource data to standardized format
   * @param {Object} data - Raw resource data
   * @returns {Object} Transformed resource data
   */
  transformResourceData(data) {
    // Extract the actual data from the nested structure
    const resourceData = data?.data || data;
    
    // Calculate system memory percentage
    const systemMemoryTotal = resourceData?.memory?.system?.total || 0;
    const systemMemoryUsed = resourceData?.memory?.system?.used || 0;
    const systemMemoryFree = resourceData?.memory?.system?.free || 0;
    const systemMemoryPercentage = systemMemoryTotal > 0 ? (systemMemoryUsed / systemMemoryTotal) * 100 : 0;
    
    return {
      systemMemory: {
        total: systemMemoryTotal,
        used: systemMemoryUsed,
        free: systemMemoryFree,
        percentage: systemMemoryPercentage,
      },
      cpu: {
        count: resourceData?.cpu?.count || 0,
        model: resourceData?.cpu?.model || 'unknown',
        speed: resourceData?.cpu?.speed || 0,
        usage: 0, // Not directly provided in your API
        loadAverage: [
          resourceData?.cpu?.loadAverage?.['1min'] || 0,
          resourceData?.cpu?.loadAverage?.['5min'] || 0,
          resourceData?.cpu?.loadAverage?.['15min'] || 0,
        ],
      },
      process: {
        memory: {
          rss: resourceData?.memory?.process?.rss || 0,
          heapTotal: resourceData?.memory?.process?.heapTotal || 0,
          heapUsed: resourceData?.memory?.process?.heapUsed || 0,
          external: resourceData?.memory?.process?.external || 0,
        },
        uptime: resourceData?.uptime?.process || 0,
      },
      platform: {
        type: resourceData?.platform?.type || 'unknown',
        arch: resourceData?.platform?.arch || 'unknown',
        version: resourceData?.platform?.release || 'unknown',
      },
      timestamp: resourceData?.timestamp || new Date().toISOString(),
    };
  }

  /**
   * Handle API errors and return standardized error format
   * @param {Error} error - Original error
   * @returns {Error} Standardized error
   */
  handleApiError(error) {
    const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error occurred';
    const statusCode = error?.response?.status || 500;
    
    return {
      message: errorMessage,
      status: statusCode,
      timestamp: new Date().toISOString(),
      originalError: error,
    };
  }

  /**
   * Check if service is healthy
   * @param {Object} healthData - Health data object
   * @returns {boolean} True if all services are healthy
   */
  isHealthy(healthData) {
    if (!healthData?.services) return false;
    
    const services = healthData.services;
    return Object.values(services).every(service => service.status === 'healthy');
  }

  /**
   * Get performance status based on metrics
   * @param {Object} performanceData - Performance data object
   * @returns {string} Status: 'good', 'warning', 'critical'
   */
  getPerformanceStatus(performanceData) {
    if (!performanceData) return 'unknown';

    const { eventLoopLatency, memory, cpu } = performanceData;
    
    // Check critical thresholds
    if (
      eventLoopLatency?.current > 100 ||
      memory?.processHeap?.percentage > 90 ||
      memory?.systemMemory?.percentage > 90 ||
      cpu?.usage > 90
    ) {
      return 'critical';
    }
    
    // Check warning thresholds
    if (
      eventLoopLatency?.current > 50 ||
      memory?.processHeap?.percentage > 70 ||
      memory?.systemMemory?.percentage > 70 ||
      cpu?.usage > 70
    ) {
      return 'warning';
    }
    
    return 'good';
  }
}

// Create and export singleton instance
const performanceApiService = new PerformanceApiService();
export default performanceApiService;

// Export individual methods for convenience
export const {
  getPerformanceMetrics,
  getHealthStatus,
  getResourceUsage,
  getAllPerformanceData,
  isHealthy,
  getPerformanceStatus,
} = performanceApiService;
